/**
 * 組織API クライアント
 */

import { authFetch, API_BASE_URL } from "./client";

/**
 * 組織レスポンスの型
 *
 * level の値の意味:
 *   - 0..9 : 上位組織階層（organization_level の first..tenth フラグに対応）
 *   - 11   : 店舗（store フラグ）
 *   - 12   : 部門（department フラグ）
 *   - 13   : 課（section フラグ）
 */
export interface OrganizationResponse {
    id: number;
    tenant_id: string;
    parent_organization_id: number | null;
    name: string | null;
    short_name: string | null;
    description: string | null;
    level: number;
}

/**
 * 組織一覧レスポンスの型
 */
export interface OrganizationListResponse {
    items: OrganizationResponse[];
    total: number;
}

/**
 * 組織作成リクエストの型
 */
export interface OrganizationCreateRequest {
    parent_organization_id?: number | null;
    name?: string | null;
    short_name?: string | null;
    description?: string | null;
}

/**
 * 組織更新リクエストの型
 */
export interface OrganizationUpdateRequest {
    parent_organization_id?: number | null;
    name?: string | null;
    short_name?: string | null;
    description?: string | null;
}

/**
 * 組織一覧を取得
 */
export async function fetchOrganizations(params?: {
    tenant_id?: string;
    parent_organization_id?: number;
}): Promise<OrganizationListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.tenant_id !== undefined) {
        searchParams.set("tenant_id", params.tenant_id);
    }

    if (params?.parent_organization_id !== undefined) {
        searchParams.set("parent_organization_id", String(params.parent_organization_id));
    }

    const queryString = searchParams.toString();
    const url = `${API_BASE_URL}/organizations${queryString ? `?${queryString}` : ""}`;

    const response = await authFetch(url);
    if (!response.ok) {
        throw new Error(`組織一覧の取得に失敗しました: ${response.status}`);
    }
    return response.json();
}

/**
 * 組織を取得
 */
export async function fetchOrganization(orgId: number): Promise<OrganizationResponse> {
    const response = await authFetch(`${API_BASE_URL}/organizations/${orgId}`);
    if (!response.ok) {
        throw new Error(`組織の取得に失敗しました: ${response.status}`);
    }
    return response.json();
}

/**
 * 組織を作成
 */
export async function createOrganization(
    tenantId: string,
    data: OrganizationCreateRequest
): Promise<OrganizationResponse> {
    const response = await authFetch(`${API_BASE_URL}/organizations/${tenantId}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw new Error(`組織の作成に失敗しました: ${response.status}`);
    }
    return response.json();
}

/**
 * 組織を更新
 */
export async function updateOrganization(
    orgId: number,
    data: OrganizationUpdateRequest
): Promise<OrganizationResponse> {
    const response = await authFetch(`${API_BASE_URL}/organizations/${orgId}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw new Error(`組織の更新に失敗しました: ${response.status}`);
    }
    return response.json();
}

/**
 * 組織を削除
 */
export async function deleteOrganization(orgId: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/organizations/${orgId}`, {
        method: "DELETE",
    });
    if (!response.ok) {
        throw new Error(`組織の削除に失敗しました: ${response.status}`);
    }
}
